// /app/frontend/src/components/frugal-v2/tabs/EmailsTab.jsx
// Pending emails tab - i18n enabled, with budget categories (250 lines)

import React, { useState, useEffect } from 'react';
import { Mail, X, Eye, Lock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/frugalApi';
import EmailDetailModal from '../components/EmailDetailModal';

const EmailsTab = ({ token, expenses = [], incomes = [], budgetCategories = [], t, onRefresh, emailsEnabled = true, onUpgrade }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [categories, setCategories] = useState(budgetCategories);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const data = await api.fetchPendingEmails(token);
      setEmails(data.emails || []);
      setBlocked(data.blocked || false);
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch budget categories if not provided
  const fetchCategories = async () => {
    if (budgetCategories.length === 0) {
      try {
        const budget = await api.fetchBudget(token);
        if (budget?.categories?.length > 0) {
          setCategories(budget.categories);
        }
      } catch (error) {
        console.error('Error fetching budget categories:', error);
      }
    }
  };

  useEffect(() => {
    if (token) {
      fetchEmails();
      fetchCategories();
    }
  }, [token]);

  const handleApprove = async (emailId, editedData = {}) => {
    if (!editedData.category || !String(editedData.category).trim()) {
      toast.error(t('frugal.emails.categoryRequired') || 'Categoria obligatoria');
      return;
    }
    setProcessingId(emailId);
    try {
      // Include account_id and transaction_type for the account selector
      await api.approveEmail(token, emailId, {
        amount: editedData.amount,
        category: editedData.category,
        transaction_type: editedData.transaction_type || 'expense',
        linked_expense_id: editedData.linked_expense_id || null,
        create_new_recurring: editedData.create_new_recurring || false,
        account_id: editedData.account_id || null
      });
      setEmails(prev => prev.filter(e => e.id !== emailId));
      setSelectedEmail(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      const msg = error?.detail || error?.message || '';
      if (msg.includes('CATEGORY_REQUIRED')) {
        toast.error(t('frugal.emails.categoryRequired') || 'Categoria obligatoria');
      } else {
        toast.error('Error al aprobar email');
      }
      console.error('Error approving email:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (emailId) => {
    setProcessingId(emailId);
    try {
      await api.rejectEmail(token, emailId);
      setEmails(prev => prev.filter(e => e.id !== emailId));
      setSelectedEmail(null);
    } catch (error) {
      console.error('Error rejecting email:', error);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse text-emerald-400">{t('frugal.status.loading')}</div>
      </div>
    );
  }

  // Show blocked state if trial expired
  if (blocked || !emailsEnabled) {
    return (
      <div className="space-y-4 pb-20">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-slate-500" />
          <h2 className="text-lg font-semibold text-slate-400">{t('frugal.emails.title')}</h2>
        </div>
        
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            {t('frugal.emails.blocked', 'Feature Blocked')}
          </h3>
          <p className="text-slate-400 text-sm mb-4">
            {t('frugal.emails.blockedDesc', 'Your free trial has ended. Upgrade to continue receiving email transactions.')}
          </p>
          
          {onUpgrade && (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => onUpgrade('monthly')}
                className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors"
              >
                <CreditCard className="w-4 h-4" />
                {t('frugal.subscription.upgradeMonthly', 'Upgrade - $2.99/month')}
              </button>
              <button
                onClick={() => onUpgrade('yearly')}
                className="flex items-center justify-center gap-2 w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
              >
                {t('frugal.subscription.upgradeYearly', 'Upgrade - $29.99/year')}
                <span className="text-emerald-400 text-xs">(Save 17%)</span>
              </button>
            </div>
          )}
        </div>
        
        <p className="text-center text-slate-500 text-xs">
          {t('frugal.emails.manualStillWorks', 'Manual expense entry is still available')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-5 h-5 text-emerald-400" />
        <h2 className="text-lg font-semibold text-white">{t('frugal.emails.title')}</h2>
        <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded-full">
          {emails.length}
        </span>
      </div>

      {/* Email List */}
      {emails.length === 0 ? (
        <div className="text-center py-12">
          <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">{t('frugal.noEmails')}</p>
          <p className="text-slate-600 text-sm mt-1">{t('frugal.noEmailsDesc')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {emails.map(email => {
            const parsed = email.parsed_data || {};
            const amount = email.amount || parsed.amount || 0;
            // Try multiple fields for merchant name, fallback to subject
            const merchant = email.merchant || email.description || parsed.merchant || email.subject || t('frugal.emails.unknownMerchant');
            const isProcessing = processingId === email.id;
            // Check if informative email (no amount)
            const isInformative = email.email_type === 'informative' || (amount === 0 && !parsed.amount);

            return (
              <div
                key={email.id}
                className={`bg-white/5 rounded-xl p-4 border ${isInformative ? 'border-blue-500/30' : 'border-white/10'}`}
              >
                {/* Email Info */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    {isInformative && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full mb-1 inline-block">
                        {t('frugal.emails.informative', 'Informativo')}
                      </span>
                    )}
                    <div className="text-white font-medium truncate">{merchant}</div>
                    {/* Show subject if different from merchant */}
                    {email.subject && email.subject !== merchant && (
                      <div className="text-slate-400 text-xs truncate mt-0.5">
                        📧 {email.subject}
                      </div>
                    )}
                    {!isInformative && (
                      <div className="text-emerald-400 font-bold">
                        ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    )}
                    {parsed.card_last_four && (
                      <div className="text-xs text-slate-500 mt-1">
                        **** {parsed.card_last_four}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-slate-500">
                      {email.created_at?.split('T')[0]}
                    </div>
                    <button
                      onClick={() => setSelectedEmail(email)}
                      className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                      title={t('frugal.emails.detail')}
                    >
                      <Eye className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                </div>

                {/* Quick Actions - different for informative vs transaction */}
                <div className="flex gap-2 mt-2">
                  {isInformative ? (
                    <>
                      <button
                        onClick={() => setSelectedEmail(email)}
                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        {t('frugal.emails.viewEmail', 'Ver Email')}
                      </button>
                      <button
                        onClick={() => handleReject(email.id)}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-white/10 hover:bg-white/20 text-slate-400 rounded-lg text-sm transition-colors disabled:opacity-50"
                        title={t('frugal.emails.markAsRead', 'Marcar como leído')}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setSelectedEmail(email)}
                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        {t('frugal.emails.viewAndApprove', 'Review & Approve')}
                      </button>
                      <button
                        onClick={() => handleReject(email.id)}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-white/10 hover:bg-white/20 text-slate-400 rounded-lg text-sm transition-colors disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Email Detail Modal */}
      {selectedEmail && (
        <EmailDetailModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          recurringExpenses={expenses}
          pendingIncomes={incomes.filter(i => i.status !== 'paid')}
          budgetCategories={categories}
          isProcessing={processingId === selectedEmail.id}
          token={token}
          t={t}
        />
      )}
    </div>
  );
};

export default EmailsTab;
